import { useFormatNumber } from '~/composables/useUtils'
import type { PrintReceiptSource, PrintSummarySection } from '../types'
import { deriveSummaryDiscountText, deriveVatText } from './labels'

export interface SummarySections {
  Summary1: PrintSummarySection | null
  Summary2: PrintSummarySection | null
  Summary3: PrintSummarySection | null
  Summary4: PrintSummarySection | null
}

/**
 * The receipt totals block, for printer interfaces >= 3.20.
 *
 * Moved verbatim from `ReceiptOrderClass.buildSummarySections`, which used to run
 * at checkout and persist its result. It runs at PRINT time now, and always
 * recomputes: a stored block on an old record was built by whatever version of
 * this function shipped that day, so honouring it would make two receipts from
 * the same shop print differently.
 *
 * Row order, labels and the section flags are the printed layout — changing them
 * changes paper. Keep byte-identical unless that is the intent.
 */
export const buildSummarySections = (
  source: PrintReceiptSource,
  mode: 'preview' | 'payment',
): SummarySections => {
  const fmt = (n: number) => useFormatNumber(n || 0, 2, true) as string
  const num = (v: unknown): number => Number(v) || 0

  const orderItems: Array<any> = Array.isArray(source?.OrderItems) ? source.OrderItems : []
  const billingRows: Array<{ name: string; value: string }> = []

  // Total item count (sum of quantities), e.g. 2 Coke + 1 Pepsi => "3 Items".
  // Always shown, on both preview and the paid receipt.
  const totalItemQty = orderItems.reduce((sum, item) => sum + (item?.quantity || 0), 0)
  billingRows.push({ name: `${totalItemQty} Items`, value: '' })

  // Sub Total reads as the gross amount BEFORE per-row item discounts; SubTotal
  // is stored gross (version 'B'), so it is used directly. itemDiscountAbs is
  // still needed for the "Item Discount" adjustment row below.
  const itemDiscountAbs = Math.abs(num(source?.ItemDiscount))
  const grossSubTotal = num(source?.SubTotal)

  const discount = num(source?.Discount)
  const serviceCharge = num(source?.ServiceCharge)
  const cardCharge = num(source?.CardCharge)
  const vat = num(source?.Vat)
  const rounding = num(source?.Rounding)
  const grandTotal = num(source?.GrandTotal)

  const discountText = deriveSummaryDiscountText(source)
  const vatText = deriveVatText(source)

  // Intermediate billing adjustments between Sub Total and Grand Total.
  const adjustmentRows: Array<{ name: string; value: string }> = []
  if (itemDiscountAbs > 0) {
    adjustmentRows.push({ name: 'Item Discount', value: `-${fmt(itemDiscountAbs)}` })
  }
  if (discount > 0) {
    const discountLabel = discountText ? `Discount ${discountText}` : 'Discount'
    adjustmentRows.push({ name: discountLabel, value: `-${fmt(discount)}` })
  }
  if (serviceCharge !== 0) {
    const scLabel = source?.ServiceChargeText
      ? `Service Charge ${source.ServiceChargeText}`
      : 'Service Charge'
    adjustmentRows.push({ name: scLabel, value: fmt(serviceCharge) })
  }
  if (cardCharge !== 0) {
    const ccLabel = source?.CardChargeText ? `Card Charge ${source.CardChargeText}` : 'Card Charge'
    adjustmentRows.push({ name: ccLabel, value: fmt(cardCharge) })
  }
  if (vat !== 0 || vatText) {
    const vatLabel = vatText ? `VAT ${vatText}` : 'VAT'
    adjustmentRows.push({ name: vatLabel, value: fmt(vat) })
  }
  if (rounding !== 0) {
    adjustmentRows.push({ name: 'Rounding', value: fmt(rounding) })
  }

  // Drop the Sub Total row when it equals Grand Total and there are no
  // intermediate adjustments — otherwise Sub Total and Grand Total print as two
  // identical lines. Show Grand Total alone in that case.
  const subtotalEqualsGrand = Math.round(grossSubTotal * 100) === Math.round(grandTotal * 100)
  if (!(subtotalEqualsGrand && adjustmentRows.length === 0)) {
    billingRows.push({ name: 'Total', value: fmt(grossSubTotal) })
  }
  billingRows.push(...adjustmentRows)

  const Summary1: PrintSummarySection = {
    Rows: billingRows,
    Underline: true,
    DoubleUnderline: false,
    Bold: false,
  }

  const Summary2: PrintSummarySection = {
    Rows: [{ name: 'Grand Total', value: fmt(grandTotal) }],
    Underline: false,
    DoubleUnderline: true,
    Bold: true,
  }

  // The payment renderer filters these two rows out and prints its own from
  // receipt.Received / receipt.Change (Graphic/ReceiptV5.cs:103-111); the
  // preview renderer does not filter. Emitted on the payment path only, as
  // before.
  let Summary3: PrintSummarySection | null = null
  if (mode === 'payment') {
    Summary3 = {
      Rows: [
        { name: 'Received', value: fmt(num(source?.Received)) },
        { name: 'Change', value: fmt(num(source?.Change)) },
      ],
      Underline: false,
      DoubleUnderline: false,
      Bold: false,
    }
  }

  // Note/Room ride along in Summary3, so on a preview (Summary3 null) they do
  // not print. Preserved as-is — see the optional-chaining no-op in the original.
  if (source?.Note) {
    Summary3?.Rows.push({ name: ' ', value: ' ' })
    if (source?.Room) {
      Summary3?.Rows.push({ name: `Room : ${source.Room}`, value: ' ' })
    }
    Summary3?.Rows.push({ name: `Note : ${source.Note}`, value: ' ' })
  }

  return { Summary1, Summary2, Summary3, Summary4: null }
}
